import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, AtSign, Music2, Timer, Send } from "lucide-react";
import { textStyleProps, countdownParts } from "./storyOverlays";
import "./storyStudio.css";

// Renderer condiviso: usato dall'editor (edit) e dallo StoryViewer (view, interattivo).
export default function StoryOverlayLayer({
  overlays,
  mode = "view",
  selectedId,
  onSelect,
  onDragElement,
  onTransformElement,
  onTapElement,
  results,
  mine,
  isOwner,
  onInteract,
  onOpenQuestion,
}) {
  if (!overlays) return null;
  const elements = overlays.elements || [];
  const strokes = overlays.strokes || [];

  return (
    <div className={`sov-layer ${mode === "edit" ? "sov-edit" : ""}`} data-testid="story-overlay-layer">
      {strokes.length > 0 && (
        <svg className="sov-strokes" viewBox="0 0 100 100" preserveAspectRatio="none">
          {strokes.map((s, i) => (
            <polyline
              key={i}
              points={(s.points || []).map((p) => `${p[0] * 100},${p[1] * 100}`).join(" ")}
              fill="none"
              stroke={s.color}
              strokeWidth={(s.width || 6) / 6}
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
              style={{ strokeWidth: s.width, filter: s.glow ? `drop-shadow(0 0 6px ${s.color})` : "none" }}
            />
          ))}
        </svg>
      )}

      {elements.map((el) => (
        <OverlayElement
          key={el.id}
          el={el}
          mode={mode}
          selected={selectedId === el.id}
          onSelect={onSelect}
          onDragElement={onDragElement}
          onTransformElement={onTransformElement}
          onTapElement={onTapElement}
          result={results?.[el.id]}
          my={mine?.[el.id]}
          isOwner={isOwner}
          onInteract={onInteract}
          onOpenQuestion={onOpenQuestion}
        />
      ))}
    </div>
  );
}

function OverlayElement({ el, mode, selected, onSelect, onDragElement, onTransformElement, onTapElement, result, my, isOwner, onInteract, onOpenQuestion }) {
  const pinchRef = useRef(null);
  const style = {
    left: `${(el.x ?? 0.5) * 100}%`,
    top: `${(el.y ?? 0.5) * 100}%`,
    transform: `translate(-50%, -50%) scale(${el.scale ?? 1}) rotate(${el.rot ?? 0}deg)`,
  };

  const startDrag = (e) => {
    if (mode !== "edit") return;
    e.stopPropagation();
    onSelect?.(el.id);
    if (pinchRef.current) return;
    const layer = e.currentTarget.parentElement;
    const rect = layer.getBoundingClientRect();
    const start = { x: e.clientX, y: e.clientY, t: Date.now() };
    let moved = false;
    let pinched = false;
    const move = (ev) => {
      if (pinchRef.current) { pinched = true; return; }
      if (Math.hypot(ev.clientX - start.x, ev.clientY - start.y) > 8) moved = true;
      if (!moved) return;
      const x = Math.min(1, Math.max(0, (ev.clientX - rect.left) / rect.width));
      const y = Math.min(1, Math.max(0, (ev.clientY - rect.top) / rect.height));
      onDragElement?.(el.id, x, y);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      if (!moved && !pinched && !pinchRef.current && Date.now() - start.t < 400) onTapElement?.(el.id);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  // due dita sull'elemento: pizzica per scalare, ruota per ruotare (come IG)
  const dist2 = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  const ang2 = (t) => Math.atan2(t[1].clientY - t[0].clientY, t[1].clientX - t[0].clientX) * 180 / Math.PI;

  const onTouchStart = (e) => {
    if (mode !== "edit" || e.touches.length !== 2) return;
    e.stopPropagation();
    pinchRef.current = { d: dist2(e.touches), a: ang2(e.touches), scale: el.scale ?? 1, rot: el.rot ?? 0 };
  };
  const onTouchMove = (e) => {
    const g = pinchRef.current;
    if (!g || e.touches.length !== 2) return;
    e.stopPropagation();
    let rot = g.rot + (ang2(e.touches) - g.a);
    const snap = Math.round(rot / 90) * 90;
    if (Math.abs(rot - snap) < 4) rot = snap;
    onTransformElement?.(el.id, {
      scale: Math.min(4, Math.max(0.3, g.scale * (dist2(e.touches) / g.d))),
      rot,
    });
  };
  const onTouchEnd = (e) => {
    if (e.touches.length < 2) pinchRef.current = null;
  };

  return (
    <div
      className={`sov-el ${selected ? "sov-selected" : ""}`}
      style={style}
      onPointerDown={startDrag}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      data-testid={`story-overlay-${el.type}-${el.id}`}
    >
      <Body el={el} mode={mode} result={result} my={my} isOwner={isOwner} onInteract={onInteract} onOpenQuestion={onOpenQuestion} />
    </div>
  );
}

// #AdETag cliccabili nei testi dei Mood (solo in visualizzazione)
function StoryTagText({ text, interactive }) {
  const navigate = useNavigate();
  const parts = String(text || "").split(/(#\w+)/g);
  if (parts.length === 1) return text;
  return parts.map((p, i) =>
    p.startsWith("#") ? (
      <span
        key={i}
        role="link"
        style={{ color: "#FFD166", textShadow: "0 0 10px rgba(255,209,102,0.45)", cursor: "pointer" }}
        onClick={interactive ? (e) => {
          e.stopPropagation();
          navigate(`/app/explore/${p.slice(1).toLowerCase()}`);
        } : undefined}
        onTouchEnd={interactive ? (e) => e.stopPropagation() : undefined}
        data-testid={`story-tag-${p.slice(1).toLowerCase()}`}
      >
        {p}
      </span>
    ) : p
  );
}

function Body({ el, mode, result, my, isOwner, onInteract, onOpenQuestion }) {
  switch (el.type) {
    case "text":
      return <div style={textStyleProps(el)}><StoryTagText text={el.text} interactive={mode !== "edit"} /></div>;

    case "emoji":
      return <div className="sov-emoji">{el.char}</div>;

    case "sticker":
      return el.kind === "logo" ? (
        <img src="/images/ade-logo-tight.png" alt="ADE" className="sov-logo" />
      ) : (
        <div className={`sov-badge ${el.kind === "pulse" ? "sov-pulse" : ""}`}>{el.label}</div>
      );

    case "mention":
      return (
        <div className="sov-chip sov-chip-mention">
          <AtSign className="w-3.5 h-3.5" />
          {el.name}
        </div>
      );

    case "location":
      return (
        <div className="sov-chip sov-chip-location">
          <MapPin className="w-3.5 h-3.5" />
          {el.label}
        </div>
      );

    case "music":
      return (
        <div className="sov-chip sov-chip-music">
          <Music2 className="w-3.5 h-3.5 sov-note" />
          {el.name}
        </div>
      );

    case "countdown":
      return <Countdown el={el} />;

    case "poll":
      return <Poll el={el} mode={mode} result={result} my={my} isOwner={isOwner} onInteract={onInteract} />;

    case "slider":
      return <SliderSticker el={el} mode={mode} result={result} my={my} isOwner={isOwner} onInteract={onInteract} />;

    case "question":
      return (
        <button
          className="sov-card sov-question"
          onClick={(e) => { if (mode === "view" && !isOwner) { e.stopPropagation(); onOpenQuestion?.(el); } }}
          data-testid={`story-question-${el.id}`}
        >
          <p className="sov-card-label">DOMANDA</p>
          <p className="sov-card-title">{el.prompt}</p>
          <span className="sov-question-cta">
            {my?.text ? "Risposta inviata" : isOwner ? `${result?.total || 0} risposte` : "Rispondi..."}
          </span>
        </button>
      );

    default:
      return null;
  }
}

function Countdown({ el }) {
  const [parts, setParts] = useState(() => countdownParts(el.target_iso));
  useEffect(() => {
    const iv = setInterval(() => setParts(countdownParts(el.target_iso)), 1000);
    return () => clearInterval(iv);
  }, [el.target_iso]);
  if (!parts) return null;
  return (
    <div className="sov-card sov-countdown">
      <p className="sov-card-label">
        <Timer className="w-3 h-3 inline mr-1" />
        {el.label || "COUNTDOWN"}
      </p>
      <div className="sov-count-row">
        {[["G", parts.d], ["H", parts.h], ["M", parts.m], ["S", parts.s]].map(([k, v]) => (
          <div key={k} className="sov-count-cell">
            <span className="sov-count-num">{String(v).padStart(2, "0")}</span>
            <span className="sov-count-unit">{k}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Poll({ el, mode, result, my, isOwner, onInteract }) {
  const counts = result?.counts || [];
  const total = counts.reduce((a, b) => a + b, 0);
  const revealed = isOwner || my?.option_index !== undefined;
  return (
    <div className="sov-card sov-poll">
      <p className="sov-card-label">SONDAGGIO</p>
      <p className="sov-card-title">{el.question}</p>
      <div className="sov-poll-options">
        {(el.options || []).map((opt, i) => {
          const pct = total > 0 ? Math.round((counts[i] || 0) * 100 / total) : 0;
          const isMine = my?.option_index === i;
          return (
            <button
              key={i}
              className={`sov-poll-opt ${isMine ? "sov-poll-mine" : ""}`}
              onClick={(e) => {
                if (mode !== "view" || isOwner) return;
                e.stopPropagation();
                onInteract?.(el, { option_index: i });
              }}
              data-testid={`story-poll-${el.id}-${i}`}
            >
              {revealed && <span className="sov-poll-fill" style={{ width: `${pct}%` }} />}
              <span className="sov-poll-text">{opt}</span>
              {revealed && <span className="sov-poll-pct">{pct}%</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SliderSticker({ el, mode, result, my, isOwner, onInteract }) {
  const initial = my?.value ?? result?.average ?? 0.5;
  const [value, setValue] = useState(initial);
  const [sent, setSent] = useState(my?.value !== undefined);
  const locked = isOwner || mode === "edit";
  const avg = result?.average;

  const commit = (e) => {
    e.stopPropagation();
    if (locked) return;
    setSent(true);
    onInteract?.(el, { value });
  };

  const pct = useMemo(() => Math.round((locked && avg != null ? avg : value) * 100), [locked, avg, value]);

  return (
    <div className="sov-card sov-slider" onPointerDown={(e) => mode === "view" && e.stopPropagation()}>
      <p className="sov-card-label">{el.prompt}</p>
      <div className="sov-slider-track">
        <span className="sov-slider-fill" style={{ width: `${pct}%` }} />
        <span className="sov-slider-thumb" style={{ left: `${pct}%` }}>{el.emoji || "🔥"}</span>
      </div>
      {!locked && (
        <>
          <input
            type="range" min={0} max={1} step={0.01} value={value}
            onChange={(e) => setValue(parseFloat(e.target.value))}
            className="sov-slider-input"
            data-testid={`story-slider-${el.id}`}
          />
          <button className="sov-slider-send" onClick={commit} data-testid={`story-slider-send-${el.id}`}>
            {sent ? "Inviato" : <><Send className="w-3 h-3" /> Invia</>}
          </button>
        </>
      )}
      {locked && avg != null && <p className="sov-slider-avg">media {pct}% · {result?.total || 0} risposte</p>}
    </div>
  );
}
